import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { inject, Injectable, Type } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CoinLetterTemplate } from '@coin/shared/cms/util';
import { Season } from '@coin/shared/season-mgmt/util';
import {
  AddPublicationDate,
  IncentiveLetterLetterPublishSelection,
  IncentiveRecordsManagerTypeEnum,
  RecordHelper,
  UpdateRecordStateDto
} from '@coin/shared/incentive/util';
import { FeatureFlagsService, HttpHelpersService, LoadingService } from '@coin/shared/core/data-access';
import {
  IncentiveRecordsWorkflowTemplateTypeEnum,
  LetterType,
  NumberFormatCountryIso,
  SeasonTypeEnum,
  WorkflowType
} from '@coin/shared/core/util-enums';
import { environment } from '@coin/shared/core/util-environments';
import { encodeSeasonType, getLetterTypeBySeasonType, RuleEngineHelper } from '@coin/shared/core/util-helpers';
import {
  LetterComment,
  MassExportTransactionStatus,
  MassReqResponse,
  PaginatedResult,
  RuleSetV2,
  TransactionStatus,
  TransactionStatusMetadata,
  Workflow
} from '@coin/shared/core/util-models';
import { ToastrService } from 'ngx-toastr';
import { filter, Observable, of, throwError } from 'rxjs';
import { catchError, delay, finalize, map, switchMap, tap } from 'rxjs/operators';

export interface CreateLetterInfos {
  recordId: string;
  templateId: string;
}

export interface CreateLetter {
  letterId: string;
  name: string;
  failureReason?: string;
}

export interface ModifiedLettersEntity {
  recordId: string;
  letterIds: string[];
}

const POLLING_DELAY_IN_MS = 4000;

@Injectable({
  providedIn: 'root'
})
export class LetterCreationService {
  private httpClient = inject(HttpClient);
  private httpHelper = inject(HttpHelpersService);
  private toast = inject(ToastrService);
  private loadingService = inject(LoadingService);
  private featureFlagService = inject(FeatureFlagsService);
  private dialog = inject(MatDialog);

  public getWorkflows(workflow?: WorkflowType): Observable<Workflow[]> {
    const filter = workflow ? `?category=${workflow}` : '';
    return this.httpClient
      .get<PaginatedResult<Workflow>>(`${environment.api.baseUrl}/admin/api/v1/master/letters/workflows${filter}`)
      .pipe(
        map(data => data?.content),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error getting workflows. ${res?.error?.reason || 'Unknown'} `);
          return throwError(`Error getting workflows. ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public getFilteredWorkflowTemplates(
    type: IncentiveRecordsWorkflowTemplateTypeEnum,
    workflowId?: string
  ): Observable<CoinLetterTemplate[]> {
    const url = workflowId
      ? `/admin/api/v1/master/letters/workflows/${workflowId}/templates?type=${type}`
      : `/admin/api/v1/master/letters/workflows/templates?type=${type}`;
    return this.httpClient.get<PaginatedResult<CoinLetterTemplate>>(environment.api.baseUrl + url).pipe(
      map(data => data?.content),
      catchError((res: HttpErrorResponse) => {
        this.toast.error(`Error getting templates. ${res?.error?.reason || 'Unknown'} `);
        return throwError(`Error getting templates. ${res?.error?.reason || 'Unknown'} `);
      })
    );
  }

  public createLetterBatchTransaction(
    recordIds: string[],
    templateId: string,
    numberFormatCountryIso: NumberFormatCountryIso,
    season: Season,
    currentProcessStepId: string
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    return this.httpClient
      .post<TransactionStatus<TransactionStatusMetadata>>(
        `${environment.api.baseUrl}/admin/api/v1/master/letters/list`,
        {
          templateId,
          recordIds,
          seasonType: season.seasonType,
          seasonId: season.id,
          letterType: getLetterTypeBySeasonType(season.seasonType, currentProcessStepId),
          numberFormatCountryIso
        }
      )
      .pipe(
        tap(() => this.toast.success('Creation of letters started.')),
        this.httpHelper.handleError('Cannot create letters'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  // TODO Unify mass creation endpoints, when BE is ready
  public massCreation(
    season: Season,
    templateId: string,
    filterText: string,
    currentProcessStepId: string,
    numberFormatCountryIso?: NumberFormatCountryIso
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    this.loadingService.present();

    const body = {
      seasonId: season.id,
      seasonType: season.seasonType,
      templateId,
      letterType: getLetterTypeBySeasonType(season.seasonType, currentProcessStepId),
      ...(numberFormatCountryIso !== undefined && { numberFormatCountryIso })
    } as {
      seasonId: string;
      seasonType: SeasonTypeEnum;
      templateId: string;
      letterType: string;
      numberFormatCountryIso?: NumberFormatCountryIso;
    };

    return this.httpClient
      .post<
        TransactionStatus<TransactionStatusMetadata>
      >(`${environment.api.baseUrl}/admin/api/v1/master/letters/batch${filterText ? `?${filterText}` : ''}`, body)
      .pipe(
        tap(() => this.toast.success('Mass creation of letters started.')),
        this.httpHelper.handleError('Cannot create letters'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public massCreationMeritV2(
    season: Season,
    templateId: string,
    numberFormatCountryIso: NumberFormatCountryIso,
    filterText: string,
    currentProcessStepId: string
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    this.loadingService.present();
    return this.httpClient
      .post<TransactionStatus<TransactionStatusMetadata>>(
        `${environment.api.baseUrl}/admin/api/v2/master/letters/seasons/${season.id}${filterText ? `?${filterText}` : ''}`,
        {
          seasonId: season.id,
          templateId,
          seasonType: season.seasonType,
          letterType: getLetterTypeBySeasonType(season.seasonType, currentProcessStepId),
          numberFormatCountryIso
        }
      )
      .pipe(
        tap(() => this.toast.success('Mass creation of letters started.')),
        this.httpHelper.handleError('Cannot create letters'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public updateLetter(
    letterId: string,
    recordId: string,
    templateId: string,
    letterName: string,
    seasonType: SeasonTypeEnum,
    letterType: LetterType
  ): Observable<CreateLetter> {
    return this.httpClient
      .patch<{ id: string; name: string }>(`${environment.api.baseUrl}/admin/api/v1/master/letters/${letterId} `, {
        id: letterId,
        templateId,
        recordId,
        seasonType,
        letterType
      })
      .pipe(
        map(() => ({ letterId, name: letterName })),
        catchError((res: HttpErrorResponse) => {
          this.loadingService.dismiss();
          return of({ failureReason: JSON.stringify(res?.error?.errors) } as CreateLetter);
        })
      );
  }

  public publishList(
    letterIds: string[],
    seasonId: string,
    selection: IncentiveLetterLetterPublishSelection,
    workflowId: string,
    managerType: IncentiveRecordsManagerTypeEnum,
    seasonType: SeasonTypeEnum,
    overriddenPublicationDates: AddPublicationDate[],
    currentProcessStepId: string
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    return this.httpClient
      .patch<TransactionStatus<TransactionStatusMetadata>>(
        `${environment.api.baseUrl}/admin/api/v1/master/letters/list/publish`,
        {
          letterIds,
          seasonId,
          workflowId,
          managerType,
          seasonType,
          letterType: getLetterTypeBySeasonType(seasonType, currentProcessStepId),
          employeeTaskTemplateId: selection?.employeeTask?.id,
          employeeMailTemplateId: selection?.employeeMail?.id,
          managerTaskTemplateId: selection?.managerTask?.id,
          managerMailTemplateId: selection?.managerMail?.id,
          overriddenPublicationDates
        }
      )
      .pipe(
        tap(() => this.toast.success('Publish started.')),
        this.httpHelper.handleError('Cannot publish letters'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public massPublish(
    seasonId: string,
    selection: IncentiveLetterLetterPublishSelection,
    workflowId: string,
    managerType: IncentiveRecordsManagerTypeEnum,
    seasonType: SeasonTypeEnum,
    overriddenPublicationDates: AddPublicationDate[],
    filterTxt: string,
    currentProcessStepId: string
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    let filterText = filterTxt;
    filterText = RecordHelper.encapsulateQuery(filterText);

    this.loadingService.present();
    return this.httpClient
      .patch<TransactionStatus<TransactionStatusMetadata>>(
        `${environment.api.baseUrl}/admin/api/v1/master/letters/batch/publish?${filterText}`,
        {
          seasonId,
          workflowId,
          managerType,
          seasonType,
          letterType: getLetterTypeBySeasonType(seasonType, currentProcessStepId),
          employeeTaskTemplateId: selection?.employeeTask?.id,
          employeeMailTemplateId: selection?.employeeMail?.id,
          managerTaskTemplateId: selection?.managerTask?.id,
          managerMailTemplateId: selection?.managerMail?.id,
          overriddenPublicationDates
        }
      )
      .pipe(
        tap(() => this.toast.success('Mass publish started.')),
        this.httpHelper.handleError('Cannot mass publish letters'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public unpublishList(letterIds: string[]): Observable<TransactionStatus<TransactionStatusMetadata>> {
    return this.httpClient
      .patch<TransactionStatus<TransactionStatusMetadata>>(
        `${environment.api.baseUrl}/admin/api/v1/master/letters/list/unpublish`,
        {
          letterIds
        }
      )
      .pipe(
        tap(() => this.toast.success('Unpublish started.')),
        this.httpHelper.handleError('Cannot unpublish letters'),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public massUnpublish(
    season: Season,
    filterTxt: string,
    currentProcessStepId: string
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    let filterText = filterTxt;
    filterText = RecordHelper.encapsulateQuery(filterText);

    this.loadingService.present();
    return this.httpClient
      .patch<TransactionStatus<TransactionStatusMetadata>>(
        `${environment.api.baseUrl}/admin/api/v1/master/letters/batch/unpublish?${filterText}`,
        {
          seasonId: season.id,
          letterType: getLetterTypeBySeasonType(season.seasonType, currentProcessStepId)
        }
      )
      .pipe(
        delay(POLLING_DELAY_IN_MS),
        tap(() => this.toast.success('Mass unpublish started.')),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error mass unpublishing. ${res?.error?.reason || 'Unknown'} `);
          return throwError(`Error mass unpublishing. ${res?.error?.reason || 'Unknown'} `);
        }),
        finalize(() => this.loadingService.dismiss())
      );
  }

  public updateRecordState(
    { recordIds, state, recordLetterState, processState, seasonId, filterText }: UpdateRecordStateDto,
    deleteLetters = true
  ): Observable<MassReqResponse> {
    return this.loadingService.withLoadingScreen(
      this.httpClient
        .patch<MassReqResponse>(
          `${environment.api.baseUrl}/admin/api/v3/master/seasons/incentive/${seasonId}/records/states${filterText ? `?${filterText}` : ''}`,
          {
            seasonId,
            state,
            recordLetterState,
            recordIds,
            processState
          }
        )
        .pipe(
          switchMap(result => {
            const letterIds = result.modifiedEntities
              ?.reduce((acc, value) => [...acc, ...(value.letterIds ?? [])], [])
              ?.filter((id: string) => !!id);
            return !letterIds?.length || !deleteLetters
              ? of(result)
              : this.deleteLetters(letterIds).pipe(map(() => result));
          }),
          tap(() => this.toast.success(`Succesfully updated record`)),
          catchError((res: HttpErrorResponse) => {
            this.toast.error(`Error updating record. ${res?.error?.reason || 'Unknown'} `);
            return throwError(`Error updating record. ${res?.error?.reason || 'Unknown'} `);
          })
        )
    );
  }

  public deleteLetters(letterIds: string[]): Observable<MassReqResponse> {
    if (!letterIds?.length) {
      return of(null);
    }

    const options = {
      headers: new HttpHeaders({
        'Content-Type': 'application/json'
      }),
      body: {
        letterIds
      }
    };
    return this.httpClient
      .delete<MassReqResponse>(`${environment.api.baseUrl}/admin/api/v1/master/letters`, options)
      .pipe(
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error deleting letters. ${res?.error?.reason || 'Unknown'} `);
          return throwError(`Error deleting letters. ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public forwardLetterTodo(letterId: string): Observable<unknown> {
    return this.httpClient
      .patch(`${environment.api.baseUrl}/admin/api/v1/master/letters/${letterId}/forward`, {
        letterId
      })
      .pipe(
        tap(() => this.toast.success('Forwarded.')),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error forwarding todo. ${res?.error?.reason || 'Unknown'} `);
          return throwError(`Error forwarding todo. ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public getComments(letterId: string): Observable<LetterComment[]> {
    return this.httpClient
      .get<LetterComment[]>(`${environment.api.baseUrl}/admin/api/v1/master/letters/${letterId}/comments`)
      .pipe(
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error getting comments. ${res?.error?.reason || 'Unknown'} `);
          return throwError(`Error getting comments. ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public postComment(letterId: string, comment: LetterComment): Observable<LetterComment> {
    comment.letterId = letterId;
    return this.httpClient
      .post<LetterComment>(`${environment.api.baseUrl}/admin/api/v1/master/letters/${letterId}/comments`, comment)
      .pipe(
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Cannot send comment: ${res?.error?.reason || 'Unknown'}`);
          return throwError(`Error sending comment ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public getLetterPreviewDocument(templateId: string): Observable<string> {
    this.loadingService.present();

    const url = this.featureFlagService.isActive('newPdfRendering')
      ? `${environment.api.pdfCreationService}/creation/templates/${templateId}/preview`
      : `${environment.api.letterServiceUrl}/letters/templates/${templateId}/preview`;
    return this.httpClient.get<{ content: string }>(url).pipe(
      map(data => data.content),
      catchError((res: HttpErrorResponse) => {
        this.toast.error(`Error getting letter. ${res?.error?.reason || 'Unknown'} `);
        return throwError(`Error getting letter. ${res?.error?.reason || 'Unknown'} `);
      }),
      finalize(() => this.loadingService.dismiss())
    );
  }

  /** @deprecated use LetterCreationService from libs folder */
  public getLetterUserDocument(letterName: string, letterId: string): Observable<string> {
    this.loadingService.present();

    return this.httpClient
      .get(`${environment.api.letterServiceUrl}/letters/${encodeURIComponent(letterName)}`, {
        responseType: 'text',
        params: {
          letterId
        }
      })
      .pipe(
        finalize(() => this.loadingService.dismiss()),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error getting letter. ${res?.error?.reason || 'Unknown'} `);
          return throwError(`Error getting letter. ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public getMultipleLetters(letterNames: string[]): Observable<unknown> {
    this.loadingService.present();
    const filteredLetterNames = letterNames.filter(letterName => !!letterName);
    return this.httpClient
      .post<{ signedUrl: string }>(`${environment.api.letterServiceUrl}/letters/url`, filteredLetterNames)
      .pipe(
        tap(result => window.open(result.signedUrl, '_blank')),
        finalize(() => this.loadingService.dismiss()),
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Error getting letters. ${res?.error?.reason || 'Unknown'} `);
          return throwError(`Error getting letters. ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public getMassMultipleLetters(
    query: string,
    seasonId: string,
    letterType: LetterType,
    seasonType: SeasonTypeEnum
  ): Observable<TransactionStatus<MassExportTransactionStatus>> {
    const endpointVersion = seasonType === SeasonTypeEnum.IncentiveV2 ? 'v3' : 'v2';
    return this.httpClient
      .post<string>(
        `${environment.api.baseUrl}/admin/api/${endpointVersion}/master/seasons/${encodeSeasonType(seasonType)}/${seasonId}/records/create-zip?${query}`,
        undefined,
        {
          params: { 'Query.LetterType': letterType }
        }
      )
      .pipe(
        map(transactionId => ({ transactionId }) as TransactionStatus<MassExportTransactionStatus>),
        this.httpHelper.handleError('Cannot download letters')
      );
  }

  public getRuleSetLetters(
    ruleSet: RuleSetV2,
    seasonId: string,
    letterType: LetterType,
    seasonType: SeasonTypeEnum
  ): Observable<TransactionStatus<MassExportTransactionStatus>> {
    const endpointVersion = seasonType === SeasonTypeEnum.IncentiveV2 ? 'v3' : 'v2';
    return this.httpClient
      .post<string>(
        `${environment.api.baseUrl}/admin/api/${endpointVersion}/master/seasons/${encodeSeasonType(seasonType)}/${seasonId}/records/create-zip`,
        RuleEngineHelper.ruleSetV2ToDto(ruleSet),
        {
          params: { 'Query.LetterType': letterType }
        }
      )
      .pipe(
        map(transactionId => ({ transactionId }) as TransactionStatus<MassExportTransactionStatus>),
        this.httpHelper.handleError('Cannot download letters')
      );
  }

  public handleFileDownload(
    metadata: MassExportTransactionStatus,
    confirmationDialogComponent: Type<unknown>
  ): Observable<unknown> {
    const filename = /zips.*/.exec(metadata.filename).at(0);

    return this.httpClient
      .get<{
        signedUrl: string;
      }>(`${environment.api.letterServiceUrl}/letters/zip`, { params: { filename } })
      .pipe(
        switchMap(({ signedUrl }) =>
          this.dialog
            .open(confirmationDialogComponent, {
              disableClose: true,
              maxWidth: 'unset',
              data: {
                headline: 'general.download-ready',
                msg: 'general.download-ready-info-message',
                cancelMsg: 'general.btnCancel',
                confirmMsg: 'general.btnDownload',
                translate: true
              }
            })
            .beforeClosed()
            .pipe(
              filter(Boolean),
              tap(() => {
                window.open(signedUrl, '_blank');
              })
            )
        )
      );
  }

  public createLetterUserDocument(letterId: string, name: string): Observable<unknown> {
    const url = this.featureFlagService.isActive('newPdfRendering')
      ? `${environment.api.pdfCreationService}/creation/${letterId}`
      : `${environment.api.letterServiceUrl}/letters/${letterId}`;
    return this.httpClient.put(url, {
      name,
      letterId
    });
  }

  public createLetterUserDocumentBatch(letters: CreateLetter[]): Observable<unknown> {
    if (!letters.length) {
      return of([]);
    }

    const url = this.featureFlagService.isActive('newPdfRendering')
      ? `${environment.api.pdfCreationService}/creation/batch`
      : `${environment.api.letterServiceUrl}/letters/batch`;
    return this.httpClient.put(url, letters);
  }

  public massCreateLetterUserDocumentBatch(
    query: string,
    seasonId: string,
    letterType: LetterType
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    return this.httpClient
      .post<TransactionStatus<TransactionStatusMetadata>>(
        `${environment.api.baseUrl}/admin/api/v1/master/letters/dry?${query}`,
        {
          seasonId,
          letterType
        }
      )
      .pipe(this.httpHelper.handleError('Cannot dry-create letters'));
  }

  public autoFinalizeLetters(body: {
    seasonId: string;
    letterIds: string[];
    message: string;
    date: string;
  }): Observable<TransactionStatus<TransactionStatusMetadata>> {
    return this.httpClient
      .post<
        TransactionStatus<TransactionStatusMetadata>
      >(`${environment.api.baseUrl}/admin/api/v1/master/letters/list/finalization`, body)
      .pipe(
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Cannot finalize letters: ${res?.error?.reason || 'Unknown'}`);
          return throwError(`Error finalizing letters ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }

  public batchAutoFinalizeLetters(
    body: { seasonId: string; message: string; date: string },
    query: string
  ): Observable<TransactionStatus<TransactionStatusMetadata>> {
    return this.httpClient
      .post<
        TransactionStatus<TransactionStatusMetadata>
      >(`${environment.api.baseUrl}/admin/api/v1/master/letters/batch/finalization?${query}`, body)
      .pipe(
        catchError((res: HttpErrorResponse) => {
          this.toast.error(`Cannot finalize letters: ${res?.error?.reason || 'Unknown'}`);
          return throwError(`Error finalizing letters ${res?.error?.reason || 'Unknown'} `);
        })
      );
  }
}
